import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, TrendingUp, Info, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import api from "@/lib/api";

const CITIES = ["Lucknow", "Noida", "Delhi", "Gurugram", "Pune", "Hyderabad", "Mumbai", "Bangalore", "Dubai", "Singapore"];
const FACINGS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];
const FURNISHING = [
  { v: "unfurnished", label: "Unfurnished" },
  { v: "semi", label: "Semi-furnished" },
  { v: "fully", label: "Fully Furnished" },
];
const TYPES = [
  { v: "apartment", label: "Apartment" },
  { v: "villa", label: "Villa / Builder Floor" },
  { v: "plot", label: "Plot / Land" },
  { v: "commercial", label: "Commercial" },
];

function fmtMoney(v, currency) {
  if (currency === "AED") return `AED ${v.toLocaleString()}`;
  if (currency === "SGD") return `SGD ${v.toLocaleString()}`;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString()}`;
}

export default function Valuation() {
  const [form, setForm] = useState({
    city: "Lucknow",
    locality: "",
    bhk: 3,
    area_sqft: 1500,
    age_years: 0,
    floor: 5,
    total_floors: 12,
    furnishing: "semi",
    facing: "east",
    amenities_count: 5,
    property_type: "apartment",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/valuation", form);
      setResult(data);
      setTimeout(() => {
        document.getElementById("valuation-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const margin = 48;
    let y = margin;

    // Header bar
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, W, 90, "F");
    doc.setFillColor(212, 164, 55); doc.rect(0, 90, W, 4, "F");
    doc.setTextColor(212, 164, 55); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text("OC INFRAVENTURES", margin, 36);
    doc.setTextColor(255, 255, 255); doc.setFontSize(20); doc.setFont("helvetica", "normal");
    doc.text("Property Valuation Report", margin, 64);
    doc.setFontSize(9); doc.setTextColor(148, 163, 184);
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
    doc.text(`Generated: ${today}`, W - margin, 36, { align: "right" });
    doc.text("UP RERA Agent: UPRERAAGT15084", W - margin, 50, { align: "right" });

    y = 130;
    // Property summary
    doc.setTextColor(100, 116, 139); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("PROPERTY DETAILS", margin, y); y += 16;
    doc.setTextColor(15, 23, 42); doc.setFontSize(10); doc.setFont("helvetica", "normal");
    const details = [
      ["City", result.city],
      ["Locality", result.locality_matched || form.locality || "—"],
      ["Configuration", `${form.bhk} BHK · ${form.property_type}`],
      ["Carpet Area", `${form.area_sqft.toLocaleString()} sqft`],
      ["Age", form.age_years === 0 ? "New / Under Construction" : `${form.age_years} yr`],
      ["Floor", form.floor === 0 ? "Ground" : `Floor ${form.floor}`],
      ["Furnishing", form.furnishing.charAt(0).toUpperCase() + form.furnishing.slice(1)],
      ["Facing", form.facing.replace("-", " ")],
      ["Amenities Count", `${form.amenities_count}`],
    ];
    details.forEach(([k, v]) => {
      doc.setTextColor(100, 116, 139); doc.text(k, margin, y);
      doc.setTextColor(15, 23, 42); doc.text(String(v), margin + 140, y);
      y += 16;
    });

    // Valuation hero
    y += 12;
    doc.setFillColor(15, 23, 42); doc.roundedRect(margin, y, W - margin * 2, 110, 8, 8, "F");
    doc.setTextColor(212, 164, 55); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("ESTIMATED FAIR VALUE", margin + 24, y + 26);
    doc.setFontSize(28); doc.setFont("helvetica", "bold");
    doc.text(fmtMoney(result.estimated_mid, result.currency), margin + 24, y + 60);
    doc.setTextColor(203, 213, 225); doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(
      `Range: ${fmtMoney(result.estimated_low, result.currency)} – ${fmtMoney(result.estimated_high, result.currency)}`,
      margin + 24, y + 84
    );
    doc.setTextColor(255, 255, 255); doc.setFontSize(9);
    doc.text(`Confidence: ${result.confidence_score}%`, W - margin - 24, y + 84, { align: "right" });
    y += 130;

    // Breakdown
    doc.setTextColor(100, 116, 139); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("CALCULATION BREAKDOWN", margin, y); y += 16;
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    Object.entries(result.breakdown).forEach(([k, v]) => {
      doc.setTextColor(100, 116, 139);
      doc.text(k.replace(/_/g, " "), margin, y);
      doc.setTextColor(15, 23, 42);
      const valStr = typeof v === "number" && v > 100 ? v.toLocaleString() : `× ${v}`;
      doc.text(valStr, W - margin, y, { align: "right" });
      y += 14;
    });

    y += 8;
    doc.setDrawColor(226, 232, 240); doc.line(margin, y, W - margin, y); y += 16;
    doc.setTextColor(100, 116, 139); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("FINAL PRICE / SQFT", margin, y);
    doc.setTextColor(212, 164, 55); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(fmtMoney(result.ppsf_used, result.currency), W - margin, y, { align: "right" });
    y += 24;

    // Disclaimer + footer
    doc.setTextColor(100, 116, 139); doc.setFontSize(7); doc.setFont("helvetica", "italic");
    const disclaimer = "Disclaimer: This valuation is an indicative estimate generated using OC Infraventures' 2026 Q1 market baseline data and weighted multi-factor model. It does not constitute a notarised valuation report. For loan / probate / capital gains purposes, please request a certified report from our RICS-empanelled panel.";
    const wrapped = doc.splitTextToSize(disclaimer, W - margin * 2);
    doc.text(wrapped, margin, y); y += wrapped.length * 9 + 6;

    // Footer
    const footY = doc.internal.pageSize.getHeight() - 40;
    doc.setFillColor(248, 250, 252); doc.rect(0, footY - 10, W, 50, "F");
    doc.setTextColor(15, 23, 42); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("ocinfraventures.com  ·  +91 522 4000 800  ·  info@ocinfraventures.com", W / 2, footY + 6, { align: "center" });
    doc.setTextColor(100, 116, 139); doc.setFont("helvetica", "normal");
    doc.text("504B Felix Square, International Business Bay, Sushant Golf City, Lucknow 226030", W / 2, footY + 20, { align: "center" });

    const fname = `OC-Valuation-${result.city}-${form.bhk}BHK-${form.area_sqft}sqft.pdf`;
    doc.save(fname);
  };

  return (
    <div data-testid="valuation-page" className="min-h-screen pb-32">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="overline mb-3 text-[#B88C2E] flex items-center gap-2"><Calculator className="w-3.5 h-3.5" /> Free Tool · No login required</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight">
            Property <span className="text-[#D4A437]">Valuation</span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-3xl text-base sm:text-lg font-light">
            Instant fair-value estimate based on 2026 market data, locality multipliers, age, floor, facing, furnishing and amenities — all in one report.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-7xl mx-auto px-6 md:px-12 mt-12 grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
          <div className="overline mb-6">Property Details</div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="City">
              <select
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                data-testid="val-city"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4A437] focus:ring-1 focus:ring-[#D4A437] outline-none"
              >
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>

            <Field label="Locality (optional)">
              <input
                type="text"
                placeholder="e.g. Sushant Golf City"
                value={form.locality}
                onChange={(e) => update("locality", e.target.value)}
                data-testid="val-locality"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4A437] focus:ring-1 focus:ring-[#D4A437] outline-none"
              />
            </Field>

            <Field label="Property Type">
              <select
                value={form.property_type}
                onChange={(e) => update("property_type", e.target.value)}
                data-testid="val-type"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4A437] focus:ring-1 focus:ring-[#D4A437] outline-none"
              >
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>

            <Field label="Configuration (BHK)">
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update("bhk", n)}
                    data-testid={`val-bhk-${n}`}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                      form.bhk === n ? "bg-[#D4A437] border-[#D4A437] text-white" : "bg-white border-slate-300 text-slate-700 hover:border-[#D4A437]"
                    }`}
                  >
                    {n === 5 ? "5+" : n} BHK
                  </button>
                ))}
              </div>
            </Field>

            <Slider label="Carpet / Built-up Area" suffix="sqft" min={200} max={10000} step={50}
              value={form.area_sqft} onChange={(v) => update("area_sqft", v)} testId="val-area" />

            <Slider label="Age of Property" suffix="yrs" min={0} max={40} step={1}
              value={form.age_years} onChange={(v) => update("age_years", v)} testId="val-age"
              format={(v) => v === 0 ? "New" : `${v} yr`} />

            <Slider label="Floor Number" suffix="" min={0} max={50} step={1}
              value={form.floor} onChange={(v) => update("floor", v)} testId="val-floor"
              format={(v) => v === 0 ? "Ground" : `Floor ${v}`} />

            <Slider label="Amenities Count" suffix="" min={0} max={15} step={1}
              value={form.amenities_count} onChange={(v) => update("amenities_count", v)} testId="val-amen"
              format={(v) => `${v} amenities`} />

            <Field label="Furnishing">
              <select value={form.furnishing} onChange={(e) => update("furnishing", e.target.value)} data-testid="val-furnishing"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4A437] focus:ring-1 focus:ring-[#D4A437] outline-none">
                {FURNISHING.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </Field>

            <Field label="Facing">
              <select value={form.facing} onChange={(e) => update("facing", e.target.value)} data-testid="val-facing"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4A437] focus:ring-1 focus:ring-[#D4A437] outline-none">
                {FACINGS.map((f) => <option key={f} value={f} className="capitalize">{f.replace("-", " ")}</option>)}
              </select>
            </Field>
          </div>

          {error && (
            <div className="mt-5 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700" data-testid="val-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="val-submit"
            className="mt-6 w-full sm:w-auto px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            {loading ? "Calculating…" : "Get Free Valuation →"}
          </button>
        </div>

        {/* How it works */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="bg-slate-900 text-white rounded-2xl p-8">
            <div className="overline text-[#D4A437] mb-3">How we estimate</div>
            <ul className="space-y-3 text-sm text-slate-200 font-light">
              <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> 2026 Q1 city baseline price/sqft</li>
              <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Locality premium auto-detected</li>
              <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Adjusts for BHK, age, floor, facing</li>
              <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Furnishing &amp; amenities premium</li>
              <li className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Returns Low / Mid / High band</li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-[#B88C2E] mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-slate-900">Note:</strong> This is an indicative estimate. For a notarised valuation report (loan / probate / capital gains), our RICS-certified panel provides a paid certified report.
                <Link to="/" className="text-[#B88C2E] font-semibold ml-1 underline decoration-dotted">Talk to expert →</Link>
              </div>
            </div>
          </div>
        </aside>
      </form>

      {/* Result */}
      {result && (
        <div id="valuation-result" className="max-w-7xl mx-auto px-6 md:px-12 mt-16" data-testid="val-result">
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl p-8 sm:p-12 text-white">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7">
                <div className="overline text-[#D4A437] mb-3">Estimated Fair Value</div>
                <div className="font-serif text-5xl sm:text-6xl text-[#D4A437] leading-none">{fmtMoney(result.estimated_mid, result.currency)}</div>
                <div className="text-slate-300 mt-3 text-sm font-light">
                  Range: <span className="text-white">{fmtMoney(result.estimated_low, result.currency)}</span>
                  <span className="mx-2">–</span>
                  <span className="text-white">{fmtMoney(result.estimated_high, result.currency)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-6 text-sm">
                  <Pill label={`${result.city}${result.locality_matched ? " · " + result.locality_matched : ""}`} />
                  <Pill label={`${fmtMoney(result.ppsf_used, result.currency)} / sqft`} />
                  <Pill label={`Confidence ${result.confidence_score}%`} positive={result.confidence_score >= 90} />
                  <button
                    type="button"
                    onClick={downloadPDF}
                    data-testid="val-download-pdf"
                    className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#D4A437] hover:bg-[#B88C2E] text-white rounded-lg text-sm font-semibold transition-colors"
                  >
                    <Download className="w-4 h-4" /> Download PDF Report
                  </button>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="overline text-slate-300 mb-3">Calculation Breakdown</div>
                <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/10">
                  {Object.entries(result.breakdown).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="font-mono text-white">{typeof v === "number" && v > 100 ? v.toLocaleString() : `× ${v}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <Link to="/insights" data-testid="val-cta-insights"
              className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-[#D4A437] hover:shadow-md transition-all">
              <TrendingUp className="w-5 h-5 text-[#D4A437]" />
              <div className="font-semibold text-slate-900 mt-3">View {result.city} Insights</div>
              <div className="text-sm text-slate-500 mt-1">Compare with city localities &amp; growth</div>
            </Link>
            <Link to="/emi-calculator" data-testid="val-cta-emi"
              className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-[#D4A437] hover:shadow-md transition-all">
              <Calculator className="w-5 h-5 text-[#D4A437]" />
              <div className="font-semibold text-slate-900 mt-3">Estimate Your EMI</div>
              <div className="text-sm text-slate-500 mt-1">See monthly outflow if you finance it</div>
            </Link>
            <Link to="/projects" data-testid="val-cta-projects"
              className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-[#D4A437] hover:shadow-md transition-all">
              <Info className="w-5 h-5 text-[#D4A437]" />
              <div className="font-semibold text-slate-900 mt-3">See Similar Projects</div>
              <div className="text-sm text-slate-500 mt-1">RERA-verified listings near you</div>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}

function Slider({ label, suffix, min, max, step, value, onChange, testId, format }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{label}</div>
        <div className="font-serif text-base text-[#D4A437]" data-testid={`${testId}-value`}>
          {format ? format(value) : `${value.toLocaleString()} ${suffix}`}
        </div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full accent-[#D4A437]" />
    </div>
  );
}

function Pill({ label, positive }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
      positive ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-white/10 text-white border border-white/15"
    }`}>{label}</span>
  );
}
